<template>
  <div class="assign-user-config">
    <RadioCardGroup
      label="Manier van toewijzen"
      :model-value="localConfig.assignmentMode"
      :options="modeOptions"
      @update:model-value="onModeChange"
    />

    <div v-if="loadingMembers" class="assign-user-config__loading">
      <LoadingSpinner size="sm" />
      <span>Teamleden laden...</span>
    </div>

    <template v-else-if="!memberOptions.length">
      <div class="alert alert--info">
        Geen teamleden gevonden. Nodig eerst collega's uit via de
        <router-link :to="{ path: '/settings/organization' }">organisatie-instellingen</router-link>.
      </div>
    </template>

    <template v-else>
      <!-- KIEZEN: één vaste persoon -->
      <template v-if="localConfig.assignmentMode === 'specific'">
        <SelectInput
          id="assign-user-single"
          label="Persoon"
          :model-value="localConfig.userIds[0] || ''"
          :options="singleSelectOptions"
          @update:model-value="onSingleSelect"
        />
        <p class="assign-user-config__hint">
          Elke nieuwe deal die de trigger raakt wordt aan deze persoon toegewezen.
        </p>
      </template>

      <!-- RANDOM: pool van personen -->
      <template v-else>
        <MultiSelectInput
          id="assign-user-pool"
          label="Personen om over te verdelen"
          :model-value="localConfig.userIds"
          :options="memberOptions"
          placeholder="Selecteer personen..."
          @update:model-value="onPoolChange"
        />
        <p class="assign-user-config__hint">
          Nieuwe deals worden willekeurig verdeeld over de gekozen personen.
          Kies minimaal twee personen voor een zinvolle verdeling.
        </p>
      </template>
    </template>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted } from 'vue';
  import RadioCardGroup from '@/components/inputs/RadioCardGroup.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import MultiSelectInput from '@/components/inputs/MultiSelectInput.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import OrganizationService from '@/services/organizationService';
  import { useUserStore } from '@/store/userStore';
  import { ASSIGNMENT_MODES } from '@/constants/automations';

  const organizationService = new OrganizationService();

  function normalizeConfig(raw) {
    const mode = raw?.assignmentMode === 'random' ? 'random' : 'specific';
    const userIds = Array.isArray(raw?.userIds) ? raw.userIds.filter(Boolean) : [];
    return { assignmentMode: mode, userIds };
  }

  export default {
    name: 'AssignUserConfig',

    components: {
      RadioCardGroup,
      SelectInput,
      MultiSelectInput,
      LoadingSpinner,
    },

    props: {
      modelValue: {
        type: Object,
        required: true,
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const userStore = useUserStore();
      const localConfig = ref(normalizeConfig(props.modelValue));
      const members = ref([]);
      const loadingMembers = ref(false);

      const modeOptions = ASSIGNMENT_MODES.map((m) => ({
        value: m.value,
        title: m.label,
        description: m.description,
        icon: m.icon,
      }));

      const memberOptions = computed(() => {
        if (!Array.isArray(members.value)) return [];
        return members.value.map((member) => {
          const user = member.user || member;
          const id = user?.id ?? member.user_id;
          const label =
            [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
            user?.email ||
            member.email ||
            id;
          return { value: id, label };
        });
      });

      // SelectInput heeft geen placeholder-prop; voeg een lege optie toe.
      const singleSelectOptions = computed(() => [
        { value: '', label: 'Kies een persoon...' },
        ...memberOptions.value,
      ]);

      const emitUpdate = () => {
        emit('update:modelValue', {
          assignmentMode: localConfig.value.assignmentMode,
          userIds: [...localConfig.value.userIds],
        });
      };

      const onModeChange = (mode) => {
        localConfig.value.assignmentMode = mode;
        // Bij wisselen naar 'specific' houden we maximaal één persoon over.
        if (mode === 'specific' && localConfig.value.userIds.length > 1) {
          localConfig.value.userIds = localConfig.value.userIds.slice(0, 1);
        }
        emitUpdate();
      };

      const onSingleSelect = (value) => {
        localConfig.value.userIds = value ? [value] : [];
        emitUpdate();
      };

      const onPoolChange = (values) => {
        localConfig.value.userIds = Array.isArray(values) ? values : [];
        emitUpdate();
      };

      const fetchMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) {
          members.value = [];
          return;
        }
        loadingMembers.value = true;
        try {
          members.value = await organizationService.getMembers(organizationId);
        } catch (error) {
          console.error('Failed to fetch members:', error);
          members.value = [];
        } finally {
          loadingMembers.value = false;
        }
      };

      onMounted(fetchMembers);

      watch(
        () => props.modelValue,
        (newVal) => {
          const next = normalizeConfig(newVal);
          if (JSON.stringify(next) !== JSON.stringify(localConfig.value)) {
            localConfig.value = next;
          }
        },
        { deep: true }
      );

      return {
        localConfig,
        modeOptions,
        memberOptions,
        singleSelectOptions,
        loadingMembers,
        onModeChange,
        onSingleSelect,
        onPoolChange,
      };
    },
  };
</script>

<style scoped lang="scss">
  .assign-user-config {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__hint {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    .alert {
      padding: 0.75rem;
      border-radius: 6px;
      font-size: 0.875rem;

      &--info {
        background: var(--blue-50);
        border: 1px solid var(--blue-200);
        color: var(--blue-700);
      }

      a {
        color: var(--blue-600);
        text-decoration: underline;
      }
    }
  }
</style>
